import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { TranslatePipe } from '@ngx-translate/core'
import { combineLatest, filter, map } from 'rxjs'
import { ToastService } from '../toast.service'
import { Router } from '@angular/router'
import { UsersService, User } from '../users.service'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { setSeconds } from 'date-fns'
import { AddMeetingData, MeetingsService } from '../meetings.service'
import { IntercomService } from '../intercom.service'
import { StepProgressComponent } from '../common/step-progress/step-progress.component'
import { DatetimePickerComponent } from '../common/datetime-picker/datetime-picker.component'
import { SelectionComponent } from '../users-selection/selection.component'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatIconButtonSizesModule } from 'mat-icon-button-sizes'
import { constructNameForUser, isDateTooFarInPastForManualMeetingCreation, isTeamsShortMeetingLink } from '../utils'

export const MAXIMUM_MEETING_DURATION_IN_MINUTES = 540
export const MINIMUM_MEETING_DURATION_IN_MINUTES = 30
export const MEETING_DURATION_INCREMENT_IN_MINUTES = 30

@Component({
  selector: 'app-add-meeting',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    ToolbarComponent,
    StepProgressComponent,
    DatetimePickerComponent,
    SelectionComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatIconButtonSizesModule,
  ],
  templateUrl: './add-meeting-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './add-meeting-page.component.scss',
})
export class AddMeetingPageComponent implements OnInit {
  private toastService = inject(ToastService)
  private router = inject(Router)
  private usersService = inject(UsersService)
  private meetingsService = inject(MeetingsService)
  intercomService = inject(IntercomService)

  currentUserQuery$ = this.usersService.getCurrentUser()
  teamUsers$ = this.usersService.getTeamUsers()

  teamUsersWithoutCurrentUser$ = combineLatest([this.currentUserQuery$, this.teamUsers$]).pipe(
    map(([currentUserResult, teamUsersResult]) => {
      if (currentUserResult.isSuccess && teamUsersResult.isSuccess) {
        const currentUser = currentUserResult.data
        const filteredUsers = teamUsersResult.data!.filter((user) => user.id !== currentUser!.id)
        return { isSuccess: true, data: filteredUsers }
      } else {
        return { isSuccess: false, data: [] }
      }
    })
  )

  stepLabels = ['MEETING_SETUP.STEPS.MEETING_DETAILS', 'MEETING_SETUP.STEPS.AGENDA_TYPE', 'MEETING_SETUP.STEPS.TEMPLATE_SOURCE', 'MEETING_SETUP.STEPS.REVIEW']

  data: AddMeetingData = {
    link: '',
    name: '',
    userIds: [],
    date: setSeconds(new Date(), 0),
    duration: 30,
  }
  isCreatingMeeting: boolean = false

  joinLiveMeetingMutation = this.meetingsService.joinLiveMeeting()

  orgUsesNativeAssistant$ = this.currentUserQuery$.pipe(
    map((result) => (result.isSuccess ? (result.data?.team.resolvedSettings.useNativeAssistant ?? false) : false))
  )

  get hasShortLink(): boolean {
    const link = this.data.link.trim()
    return link !== '' && isTeamsShortMeetingLink(link)
  }

  get isCreateButtonDisabled(): boolean {
    return this.data.name.trim() === '' || this.data.link.trim() === '' || this.isDateTooFarInPast(this.data.date)
  }

  get increaseDurationButtonDisabled(): boolean {
    return this.data.duration >= MAXIMUM_MEETING_DURATION_IN_MINUTES
  }

  get decreaseDurationButtonDisabled(): boolean {
    return this.data.duration <= MINIMUM_MEETING_DURATION_IN_MINUTES
  }

  isDateTooFarInPast = isDateTooFarInPastForManualMeetingCreation

  ngOnInit() {
    this.joinLiveMeetingMutation.result$
      .pipe(
        filter((result) => result.isSuccess),
        map((result) => result.data)
      )
      .subscribe((meeting) => {
        this.toastService.show('UPLOAD_MEETING.IMPORT_SUCCESSFUL_NOTIFICATION')
        this.router.navigate(['/app', 'meetings', meeting!.id])
      })
  }

  addFullNameToUserList(users: User[]) {
    return users.map((user) => {
      return {
        ...user,
        fullName: constructNameForUser(user),
      }
    })
  }

  onUserSelectionChange(users: User[]) {
    this.data.userIds = users.map((user) => user.id)
  }

  increaseDuration() {
    if (this.data.duration < MAXIMUM_MEETING_DURATION_IN_MINUTES) {
      this.data.duration += MEETING_DURATION_INCREMENT_IN_MINUTES
    }
  }

  decreaseDuration() {
    if (this.data.duration > MINIMUM_MEETING_DURATION_IN_MINUTES) {
      this.data.duration -= MEETING_DURATION_INCREMENT_IN_MINUTES
    }
  }

  onCreateMeetingClicked() {
    this.joinLiveMeetingMutation.mutate(this.data)
  }
}
